import { useCallback, useEffect, useState } from "react";
import Button from "../components/Button";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import {
  setCurrentPage,
  setPerPage,
  setLoading,
  setRegion,
  setSearchTerm,
  setTotalPage,
  setTotalCount 
} from "../generatedSlice/regionSlice";
import useSearchApi from "../customHooks/useSearchApi";
import { getRegion } from "../app/request/regionRequest";
import { toast } from "react-toastify";
import TableSkeleton from "../components/TableSkeleton";
import RegionsList from "./RegionsList";
import { TTableProps } from "../types/TableView";
import { useNavigate } from "react-router-dom";
import { TOptionType } from "../types/CustomSelect";
import RegionFilters from "../generatedFilters/RegionFilters";
import {
  TDataObjectType,
  ThandleAfterSearchData,
} from "../types/Module/Region/RegionsPage";
import { columns } from "./columns";
import { toAbsoluteUrl } from "../_metronic/helpers";


const RegionsPage = () => {
  const [showTooltip, setShowTooltip] = useState(false);
  const dispatch = useDispatch();
  const [initload, setInitload] = useState(true);
  const [filterloading, setFilterLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<TOptionType<string>>({
    label: "",
    value: "",
  });
  const [languageFilter, setLanguageFilter] = useState("");

  const {
    data,
    perPage,
    currentPage,
    totalPage,
    searchTerm,
    loading,
    rerender1,
    totalCount
  } = useSelector((state: RootState) => state.region);

  useEffect(() => {
    const fn = async () => {
      const searchQuery = `page=${currentPage}&limit=${perPage}&search=${searchTerm}&status=${statusFilter?.value}&languageId=${languageFilter}&showNeutral=true`;
      dispatch(setLoading(true));
      try {
        const response = await getRegion(searchQuery);
        if (response.data.status) {
          dispatch(setRegion(response.data.data.data));
           dispatch(setTotalCount(Math.ceil(response.data.data.totalCount)));
          dispatch(
            setTotalPage(Math.ceil(response.data.data.totalCount / perPage))
          );
        } else {
          toast.error("Something went wrong");
        }
      } catch (error) {
        toast.error("Network Error");
      } finally {
        dispatch(setLoading(false));
        setInitload(false);
        if (filterloading) {
          setFilterLoading(false);
        }
      }
    };

    fn();
  }, [rerender1, dispatch, statusFilter, languageFilter]);

  useEffect(() => {
    return () => {
      dispatch(setSearchTerm(""));
    };
  }, []);

  function handleAfterSearch(
    isSuceess: boolean,
    data: ThandleAfterSearchData,
    value: number
  ) {
    dispatch(setRegion(data.data.data.data));
    dispatch(setTotalPage(Math.ceil(data.data.data.totalCount / value)));
  }

  const { doSearch, searchLoading } = useSearchApi(
    getRegion,
    handleAfterSearch
  );

  const handleSearchParams = useCallback(
    (value: string) => {
      dispatch(setCurrentPage(1));
      dispatch(setSearchTerm(value));
      const searchQuery = `page=${"1"}&limit=${perPage}&search=${value}&status=${
        statusFilter?.value
      }&languageId=${languageFilter}`;
      doSearch(searchQuery, perPage);
    },
    [perPage, dispatch, doSearch, languageFilter, statusFilter?.value]
  );

  const handleStatusFilter = useCallback((data: TOptionType<string>) => {
    dispatch(setCurrentPage(1));
    setStatusFilter(data);
    setFilterLoading(true);
  }, []);

  const handleLanguageFilter = useCallback((data: TDataObjectType[]) => {
    let payload = data.map((value) => value.id).join(",");
    dispatch(setCurrentPage(1));
    setLanguageFilter(payload);
    setFilterLoading(true);
  }, []);

  const handlePageChange = useCallback(
    (value: number) => {
      dispatch(setCurrentPage(value));
      const searchQuery = `page=${value}&limit=${perPage}&search=${searchTerm}&status=${statusFilter?.value}&languageId=${languageFilter}`;
      doSearch(searchQuery, perPage);
    },
    [
      dispatch,
      perPage,
      searchTerm,
      doSearch,
      languageFilter,
      statusFilter?.value,
    ]
  );

  const handlePerPageChange = useCallback(
    (value: number) => {
      dispatch(setPerPage(value));
      const searchQuery = `page=${currentPage}&limit=${value}&search=${searchTerm}&status=${statusFilter?.value}&languageId=${languageFilter}`;
      doSearch(searchQuery, value);
    },
    [
      dispatch,
      currentPage,
      searchTerm,
      doSearch,
      languageFilter,
      statusFilter?.value,
    ]
  );

  const configuration: TTableProps = {
    columns: columns,
    rows: data,
    showPagination: true,
    showSearch: true,
    showPerPage: true,
    paginationData: {
      perPage: perPage,
      currentPage: currentPage,
      totalPage: totalPage,
      totalCount: totalCount,

    },
    showFilter: true,
    onPageChange: handlePageChange,
    onPerPageChange: handlePerPageChange,
    handleSearchParams: handleSearchParams,
    searchPlaceholder: "Search Regions",
    searchLoading: searchLoading || filterloading,
    filters: (
      <RegionFilters
        onStatusFilterChange={handleStatusFilter}
        onLanguageFilterChange={handleLanguageFilter}
        currentLanguageFilter={languageFilter}
        currentStatusFilter={statusFilter}
      />
    ),
  };
  const navigate = useNavigate();

  if (loading && initload) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-5">
        <div className="d-flex align-items-center" style={{ gap: "5px" }}>
          <h2>Regions </h2>

          <img
            alt="Logo"
            src={toAbsoluteUrl("/media/icons/duotune/art/exclamation-icon.svg")}
            className="h-20px cursor-pointer"
            onClick={()=>navigate(`/helps?moduleName=Region`)}

          />
      
        </div>

        <Button
          variant="primary"
          size="medium"
          handleButtonClick={() => navigate(`add`)}
        >
          Add Region
        </Button>
      </div>
      <RegionsList {...configuration} />
    </>
  );
};

export default RegionsPage;
